import { Injectable } from '@angular/core';
import {
  ActivatedRoute,
  NavigationExtras,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { CookieUtilWrapperService } from './cookie-util-wrapper.service';

@Injectable({
  providedIn: 'root',
})
export class RedirectOnLoginService {
  private redirectURLExpiry: number = 60000;
  private snapshotURL: RouterStateSnapshot;
  regexList = [
    {
      pattern:
        '/settings/overlay/notification-center/subscription/edit/[a-zA-Z0-9]+/edit/payload$',
      fallbackURL: '/settings/overlay/notification-center/subscriptions',
    },
    {
      pattern:
        '/settings/overlay/notification-center/subscription/create/payload$',
      fallbackURL: '/settings/overlay/notification-center/subscription/create',
    },
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cookieUtilService: CookieUtilWrapperService,
  ) {}

  // preserveRedirectURL method stores the last visited URL in a cookie to be used for redirection later.
  // The URL is taken from the current router URL (this.router.url), or if it's empty, it falls back to the snapshot URL.
  preserveRedirectURL() {
    return new Promise((resolve, reject) => {
      // Get the URL to preserve, preferring the snapshot URL if available and falling back to the current router URL.
      let url = window.location.pathname;

      if (url?.substring(0, 5) === '/capc') {
        url = url?.substring(5);
      }

      // Do not store '/' or '/home' or '/no-access'
      if (
        url === '/' ||
        url === '/home' ||
        url === '/no-access' ||
        url === '/sso-logout' ||
        url === '/login'
      ) {
        resolve(true);
      }
      // Check if the URL matches any of the specified regex patterns in the regexList.
      else {
        const fallBackURL = this.matchAndFallbackURL(url, this.regexList);
        // If a fallback URL is found, store it in the cookie; otherwise, store the original URL.
        fallBackURL
          ? this.cookieUtilService.set(
              'redirectTo',
              fallBackURL,
              this.redirectURLExpiry,
              '/',
            )
          : this.cookieUtilService.set(
              'redirectTo',
              url,
              this.redirectURLExpiry,
              '/',
            );
        resolve(true);
      }
    });
  }

  getRedirectURL() {
    return this.cookieUtilService.get('redirectTo');
  }

  handleRedirectURL(isLogin: boolean = true) {
    let redirectURL = this.getRedirectURL();
    // If user is coming from login page, then navigate him to redirectURL or to home page
    if (isLogin) {
      redirectURL
        ? this.navigateToUrl(redirectURL)
        : this.router.navigate(['/home']);
    } else {
      // If user is refreshing the page and there is no redirectURL then do not navigate anywhere and stay in same page
      if (redirectURL) {
        this.navigateToUrl(redirectURL);
      }
    }
  }

  matchAndFallbackURL(url, regexList) {
    for (let i = 0; i < regexList.length; i++) {
      const regex = new RegExp(regexList[i].pattern);
      if (regex.test(url)) {
        return regexList[i]?.fallbackURL;
      }
    }
    return null; // or return the original URL if no match is found
  }

  navigateToUrl(url: string): void {
    const hasQueryParams = url.includes('?');
    if (hasQueryParams) {
      const [routeUrl, queryParams] = url.split('?');
      const navigationExtras: NavigationExtras = {
        queryParams: this.parseQueryParams(queryParams),
        relativeTo: this.route,
      };
      this.router.navigate([routeUrl], navigationExtras);
    } else {
      this.router.navigate([url]);
    }
  }

  parseQueryParams(queryParams: string): any {
    const params = {};
    const paramPairs = queryParams.split('&');
    paramPairs.forEach((pair) => {
      const [key, value] = pair.split('=');
      params[key] = value;
    });
    return params;
  }
}
